/**
 * Copyright (C) Siemens AG 1996-2026 ALL RIGHTS RESERVED.
 */

import {UrlMatcher, UrlSegment} from '@angular/router';

/**
 * Identifier standards the public viewer can resolve from a browser URL.
 *
 * - `iec61406` — IEC 61406-1 Identification Link. Entry points: the long
 *   canonical form `/public/iec61406/{RA}/{Issuer}/{ObjectId}` (BL-5936) and
 *   the short form `/id/{ObjectId}` (BL-6190).
 * - `gs1` — GS1 Digital Link URI keyed on one of the supported primary keys,
 *   e.g. `/01/{GTIN}[/21/{serial}]` (BL-6190).
 */
export type ProductIdStandard = 'iec61406' | 'gs1';

/** First path segment of the short IEC 61406 form (`/id/…`). */
export const IEC61406_SHORT_PREFIX = 'id';

/**
 * GS1 Digital Link primary keys accepted as first path segment, as GS1
 * application identifiers or their alphabetic aliases:
 * `01` GTIN, `gtin` (alias), `00` SSCC, `253` GDTI, `8004` GIAI, `8006` ITIP.
 */
export const GS1_PRIMARY_KEY_PREFIXES: readonly string[] = [
  '01',
  'gtin',
  '00',
  '253',
  '8004',
  '8006',
];

const IEC61406_LONG_PREFIX = ['public', 'iec61406'];

/**
 * All short-form entry-point prefixes routed to the public viewer: the short
 * IEC 61406 form and every GS1 Digital Link primary key. Single source of
 * truth for `app-routing.module.ts`'s combined matcher route, so the route
 * table and `detectProductIdStandard()` cannot drift apart (BL-6190).
 */
export const PRODUCT_ID_ENTRY_PREFIXES: readonly string[] = [
  IEC61406_SHORT_PREFIX,
  ...GS1_PRIMARY_KEY_PREFIXES,
];

/**
 * Builds an Angular `UrlMatcher` that consumes exactly one leading segment
 * when it equals one of `prefixes`, compared case-insensitively. The
 * remaining segments are left for a `**` child route, so the segment layout
 * after the prefix is not constrained (IEC 61406-1 leaves it to the creator
 * and GS1 Digital Links have a variable number of qualifier pairs).
 *
 * @param prefixes - Accepted first segments, lower case
 * @returns A matcher usable in a `Route.matcher` property
 */
export function productIdPrefixMatcher(
  prefixes: readonly string[],
): UrlMatcher {
  return (segments: UrlSegment[]) =>
    segments.length > 0 && prefixes.includes(segments[0].path.toLowerCase())
      ? {consumed: [segments[0]]}
      : null;
}

/**
 * Detects whether a browser pathname is a Product ID entry point and, if so,
 * which identifier standard it follows.
 *
 * Only the pathname is inspected and only its leading prefix is interpreted,
 * case-insensitively. The full browser URL (including query parameters such
 * as `q` and `v`) is what gets forwarded to the backend, so nothing is parsed
 * or rebuilt here — this function only decides *which* lookup the viewer
 * performs (ADR-074).
 *
 * A prefix with no non-empty segment after it (`/id`, `/id/`, `/id//x`) is
 * not a Product ID, so no backend request is made for it.
 *
 * @param pathname - `window.location.pathname`
 * @returns The matched standard, or `null` when the path is not a Product ID entry point
 */
export function detectProductIdStandard(
  pathname: string,
): ProductIdStandard | null {
  const segments = pathname.replace(/^\/+/, '').split('/');
  const first = segments[0]?.toLowerCase() ?? '';

  if (
    first === IEC61406_LONG_PREFIX[0] &&
    segments[1]?.toLowerCase() === IEC61406_LONG_PREFIX[1]
  ) {
    return hasObjectSegment(segments, 2) ? 'iec61406' : null;
  }
  if (first === IEC61406_SHORT_PREFIX) {
    return hasObjectSegment(segments, 1) ? 'iec61406' : null;
  }
  if (GS1_PRIMARY_KEY_PREFIXES.includes(first)) {
    return hasObjectSegment(segments, 1) ? 'gs1' : null;
  }

  return null;
}

/** True when the segment at `index` exists and is not empty. */
function hasObjectSegment(segments: string[], index: number): boolean {
  return (segments[index] ?? '') !== '';
}
